import React from 'react';
import Money from './Money';

/**
 * Ranking de mozos por lo que les tocó: posición, nombre, monto y una barra
 * proporcional al primero.
 *
 * Reemplaza a la torta que había en Estadísticas. Una torta obliga a comparar
 * ángulos, que es lo que peor hace el ojo: con ocho mozos es imposible decir
 * quién sacó más entre el tercero y el cuarto. Además su paleta se ciclaba
 * (`COLORS[i % 10]`), así que a partir del mozo 11 dos personas distintas
 * compartían color. Acá el orden y el largo de la barra dicen todo y el color
 * no carga información.
 *
 * `top` recorta la lista (el dashboard muestra 5, Estadísticas los muestra
 * todos); `mostrarTotal` agrega el porcentaje sobre el total repartido.
 */
export default function RankingMozos({ items = [], top, mostrarPorcentaje = false }) {
  const ordenados = [...items].sort((a, b) => b.total - a.total);
  const visibles = top ? ordenados.slice(0, top) : ordenados;
  const max = ordenados[0]?.total || 1;
  const totalGeneral = ordenados.reduce((s, e) => s + e.total, 0) || 1;

  if (visibles.length === 0) {
    return (
      <div className="si-caption py-6 text-center">
        Todavía no hay repartos en este período.
      </div>
    );
  }

  return (
    <ol className="space-y-3">
      {visibles.map((emp, idx) => {
        // Mínimo de 4% para que a quien sacó muy poco igual se le vea la barra.
        const ancho = Math.max(4, (emp.total / max) * 100);
        // El podio se distingue por color; del cuarto en adelante, todos igual.
        const podio = [
          { bg: '#c8f06a', fg: '#0c0c0f' },
          { bg: '#b28cf5', fg: '#0c0c0f' },
          { bg: '#f59e0b', fg: '#0c0c0f' },
        ];
        // Fuera del podio va un gris que igual se lee sobre la pista: el
        // #26262e original era casi el mismo color que el fondo de la barra,
        // asi que del cuarto puesto para abajo no se veia nada.
        const c = podio[idx] || { bg: '#4a4a58', fg: '#e6e6ea' };

        return (
          <li key={`${emp.nombre}-${emp.apellido}-${idx}`} className="flex items-center gap-3">
            <div
              className="w-7 h-7 rounded-lg grid place-items-center text-xs font-bold flex-shrink-0 si-money"
              style={{ background: c.bg, color: c.fg }}
            >
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-sm text-ink truncate">
                  {emp.nombre} {emp.apellido}
                </div>
                <div className="flex items-baseline gap-2 whitespace-nowrap">
                  {mostrarPorcentaje && (
                    <span className="text-xs text-ink-2 si-money">
                      {((emp.total / totalGeneral) * 100).toFixed(1).replace('.', ',')}%
                    </span>
                  )}
                  <span className="text-sm font-semibold text-ink">
                    <Money value={emp.total} />
                  </span>
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${ancho}%`, background: c.bg }}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
